import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import FullWidthCarouselWithAnalytics from 'components/WithAnalytics/Carousel/FullWidthCarouselWithAnalytics'
import useOfferList from 'hooks/Offers/useOfferList'
import type { ComponentProps, PropsWithChildren } from 'react'
import { memo, useEffect, useRef } from 'react'
import { useInView } from 'react-intersection-observer'
import OfferListCarouselTrack from './OfferListCarouselTrack'
import { setLogicalScrollLeft } from 'LuxKit/Carousel/utils'

type InheritedOfferListCarouselTrackProps = Pick<
  ComponentProps<typeof OfferListCarouselTrack>,
  'additionalOfferUrlQuery' | 'tabletLimit' | 'limit' | 'tileStyle' | 'tileSize' | 'eagerLoadImages' | 'offerDistances' | 'offerDistanceFrom'
>

interface Props extends PropsWithChildren, InheritedOfferListCarouselTrackProps {
  /**
   * The set of filters to fetch the offer list with
   */
  filters?: App.OfferListFilters
  /**
   * Use this to provide your own full offer list for the carousel to display
   */
  overrideOfferList?: App.OfferList
  className?: string
  /**
   * Exclude the following offer ids from the results
   */
  excludedIds?: Array<string>
  /**
   * - `block`: To be used when the carousel is contained inside another element. (ie. modal or dropdown)
   * - `page`: To be used as an immediate child of a page. (like a page section)
   *
   * @default page
   */
  size?: 'block' | 'page'
  /**
   * Override the duration and the price with the checkIn/checkOut/Occupancy
   */
  selectedDatesAndRoomsFilter?: App.OfferListFilters
}

function OfferListCarousel(props: Props) {
  const {
    filters,
    overrideOfferList,
    limit,
    tabletLimit,
    children,
    tileStyle = 'card-default',
    tileSize = 'small',
    additionalOfferUrlQuery,
    className,
    excludedIds,
    size = 'page',
    selectedDatesAndRoomsFilter,
    eagerLoadImages,
    offerDistances,
    offerDistanceFrom,
  } = props

  const filterOfferList = useOfferList(
    filters,
    {
      disabled: !!overrideOfferList || !filters,
      excludedIds,
      minRequiredOffers: filters?.limit,
    },
  )
  const offerList = overrideOfferList ?? filterOfferList
  const carouselRef = useRef<HTMLDivElement | null>(null)

  const [inViewRef, carouselInView] = useInView({
    triggerOnce: true,
    rootMargin: '500px',
  })

  useEffect(() => {
    // Offer ids can update incrementally while cards load. Only reset when the
    // offer-list query changes or finishes loading.
    if (!offerList.fetching && carouselRef.current) {
      setLogicalScrollLeft(carouselRef.current, 0)
    }
  }, [offerList.fetching, offerList.key])

  useEffect(() => {
    inViewRef(carouselRef.current)
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselRef.current])

  const track = <OfferListCarouselTrack
    inView={carouselInView}
    offerList={offerList}
    tileStyle={tileStyle}
    tileSize={tileSize}
    additionalOfferUrlQuery={additionalOfferUrlQuery}
    filters={filters}
    limit={limit}
    tabletLimit={tabletLimit}
    selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
    eagerLoadImages={eagerLoadImages}
    offerDistances={offerDistances}
    offerDistanceFrom={offerDistanceFrom}
  >
    {children}
  </OfferListCarouselTrack>

  if (size === 'page') {
    let itemsPerArrow = 1
    if (tileStyle === 'card-default' || tileStyle === 'card-opaque' || tileStyle === 'card-locked') {
      if (tileSize === 'small') {
        itemsPerArrow = 3
      } else if (tileSize === 'medium') {
        itemsPerArrow = 2
      }
    }

    return <FullWidthCarouselWithAnalytics
      itemsPerArrow={itemsPerArrow}
      snap="start"
      className={className}
      ref={carouselRef}
    >
      {track}
    </FullWidthCarouselWithAnalytics>
  }

  return <CardCarousel
    snap="start"
    className={className}
    ref={carouselRef}
  >
    {track}
  </CardCarousel>
}

export default memo(OfferListCarousel)
